import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { POST_LOGIN_ROUTE } from '@/features/auth/routes';
import { useNotify } from '@/hooks/useNotify';
import type { Rol } from '@/types/enums';

interface RoleGuardProps {
  children: ReactNode;
  /** Roles allowed to view the children. If user's role is not in this list, redirects. */
  allowedRoles: readonly Rol[];
  /** Where to redirect if the role is not allowed. Defaults to POST_LOGIN_ROUTE. */
  redirectTo?: string;
  /** Optional info notification to show when access is denied. */
  denegadoMessage?: string;
}

/** Handles redirect + notification when access is denied. */
function AccessDeniedRedirect({ to, message }: { to: string; message: string }): null {
  const notify = useNotify();
  const navigate = useNavigate();
  const doneRef = useRef(false);

  useEffect(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    notify.info(message);
    navigate(to, { replace: true });
  }, [message, navigate, notify, to]);

  return null;
}

/**
 * Restrict access to a route based on the user's role.
 *
 * Must be wrapped inside a ProtectedRoute (which handles authentication).
 * This component only handles role-based authorization.
 */
export function RoleGuard({
  children,
  allowedRoles,
  redirectTo = POST_LOGIN_ROUTE,
  denegadoMessage,
}: RoleGuardProps): JSX.Element {
  const { currentUser } = useAuth();

  if (!currentUser) {
    return <Navigate to="/" replace />;
  }

  if (!allowedRoles.includes(currentUser.rol as Rol)) {
    if (denegadoMessage) {
      return <AccessDeniedRedirect to={redirectTo} message={denegadoMessage} />;
    }
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
